import React, { useState } from 'react';
import {
  Box,
  Stack,
  Button,
  Card,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import Iconify from '../minimal/iconify/Iconify';
import { useLocales } from '../../locales';
import { format } from 'date-fns';

export const DateOverridesForm = ({ overrides = [], onChange }) => {
  const { translate } = useLocales();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [currentOverride, setCurrentOverride] = useState({
    date: '',
    schedule: null,
    reason: ''
  });

  const handleOpenDialog = (override = null, index = null) => {
    if (override) {
      setCurrentOverride(override);
      setEditIndex(index);
    } else {
      setCurrentOverride({
        date: '',
        schedule: null,
        reason: ''
      });
      setEditIndex(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditIndex(null);
    setCurrentOverride({
      date: '',
      schedule: null,
      reason: ''
    });
  };

  const handleSaveOverride = () => {
    if (!currentOverride.date || !currentOverride.reason) {
      return;
    }

    const newOverrides = [...overrides];

    if (editIndex !== null) {
      newOverrides[editIndex] = currentOverride;
    } else {
      newOverrides.push(currentOverride);
    }

    onChange(newOverrides);
    handleCloseDialog();
  };

  const handleRemoveOverride = (index) => {
    const newOverrides = overrides.filter((_, i) => i !== index);
    onChange(newOverrides);
  };

  const formatTimeDisplay = (time) => {
    if (!time) return '';
    const hour = time.substring(0, 2);
    const min = time.substring(2, 4);
    return `${hour}:${min}`;
  };

  const handleTimeChange = (field, value) => {
    const cleanValue = value.replace(/[^0-9]/g, '');
    if (cleanValue.length <= 4) {
      setCurrentOverride({
        ...currentOverride,
        schedule: {
          ...currentOverride.schedule,
          [field]: cleanValue
        }
      });
    }
  };

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">
          {translate('event_settings.special_dates')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Iconify icon="eva:plus-fill" />}
          onClick={() => handleOpenDialog()}
        >
          {translate('event_settings.add_special_date')}
        </Button>
      </Stack>

      {overrides.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{translate('event_settings.date')}</TableCell>
                <TableCell>{translate('event_settings.status')}</TableCell>
                <TableCell>{translate('event_settings.reason')}</TableCell>
                <TableCell>{translate('event_settings.hours')}</TableCell>
                <TableCell align="right">{translate('general.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {overrides.map((override, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Typography variant="body2">
                      {override.date}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={override.schedule === null
                        ? translate('general.closed')
                        : translate('event_settings.modified_hours')}
                      color={override.schedule === null ? 'error' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{override.reason}</TableCell>
                  <TableCell>
                    {override.schedule
                      ? `${formatTimeDisplay(override.schedule.startTime)} - ${formatTimeDisplay(override.schedule.endTime)}`
                      : '-'
                    }
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(override, index)}
                    >
                      <Iconify icon="eva:edit-2-outline" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveOverride(index)}
                    >
                      <Iconify icon="eva:trash-2-outline" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity="info">
          {translate('event_settings.no_special_dates')}
        </Alert>
      )}

      {/* Add/Edit Override Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editIndex !== null
            ? translate('event_settings.edit_special_date')
            : translate('event_settings.add_special_date')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <DatePicker
              label={translate('event_settings.select_date')}
              value={currentOverride.date ? new Date(currentOverride.date) : null}
              onChange={(date) => setCurrentOverride({
                ...currentOverride,
                date: date ? format(date, 'yyyy-MM-dd') : ''
              })}
              renderInput={(params) => <TextField {...params} fullWidth />}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true
                }
              }}
            />

            <TextField
              fullWidth
              label={translate('event_settings.reason')}
              value={currentOverride.reason}
              onChange={(e) => setCurrentOverride({
                ...currentOverride,
                reason: e.target.value
              })}
              placeholder={translate('event_settings.reason_placeholder')}
              required
            />

            <FormControlLabel
              control={
                <Switch
                  checked={currentOverride.schedule !== null}
                  onChange={(e) => setCurrentOverride({
                    ...currentOverride,
                    schedule: e.target.checked
                      ? {
                          enabled: true,
                          startTime: "1000",
                          endTime: "1600",
                          slotDuration: 30,
                          slotCapacity: 10,
                          breakTimes: []
                        }
                      : null
                  })}
                />
              }
              label={translate('event_settings.custom_hours_toggle')}
            />

            {currentOverride.schedule && (
              <>
                <Alert severity="info">
                  {translate('event_settings.custom_hours_info')}
                </Alert>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label={translate('event_settings.opening_time')}
                      value={formatTimeDisplay(currentOverride.schedule.startTime)}
                      onChange={(e) => handleTimeChange('startTime', e.target.value)}
                      placeholder="10:00"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label={translate('event_settings.closing_time')}
                      value={formatTimeDisplay(currentOverride.schedule.endTime)}
                      onChange={(e) => handleTimeChange('endTime', e.target.value)}
                      placeholder="16:00"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label={translate('event_settings.slot_duration_minutes')}
                      value={currentOverride.schedule.slotDuration}
                      onChange={(e) => setCurrentOverride({
                        ...currentOverride,
                        schedule: {
                          ...currentOverride.schedule,
                          slotDuration: parseInt(e.target.value) || 30
                        }
                      })}
                      InputProps={{ inputProps: { min: 15, max: 480 } }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label={translate('event_settings.capacity_per_slot')}
                      value={currentOverride.schedule.slotCapacity}
                      onChange={(e) => setCurrentOverride({
                        ...currentOverride,
                        schedule: {
                          ...currentOverride.schedule,
                          slotCapacity: parseInt(e.target.value) || 10
                        }
                      })}
                      InputProps={{ inputProps: { min: 1, max: 1000 } }}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            {translate('general.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveOverride}
            disabled={!currentOverride.date || !currentOverride.reason}
          >
            {editIndex !== null ? translate('general.save') : translate('general.add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};